import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import type {
  MessagingServerHealth,
  MessagingServerSummary,
} from '@aeris/contracts'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Section,
  Spinner,
} from '@aeris/ui'

import {
  fetchMessagingServers,
  saveMessagingServer,
  testMessagingServer,
} from '../lib/api/platform.ts'
import { ApiError } from '../lib/api.ts'

/**
 * Os servidores de WhatsApp por aparelho vinculado — a WuzAPI (F192,
 * ADR-0039).
 *
 * Da plataforma e não da loja: o token de administração enxerga as instâncias
 * de TODOS os clientes do servidor. Ele entra por aqui e nunca volta — a
 * edição deixa o campo vazio e "vazio" quer dizer "mantenha o gravado".
 *
 * Salvar já testa: a resposta traz o `/health`, e a tela o mostra na hora. Um
 * servidor cadastrado com o endereço errado é descoberto aqui, não quando a
 * primeira loja tentar vincular um número.
 */

export const describeHealth = (health: MessagingServerHealth): string =>
  health.reachable
    ? [
        'Respondeu',
        health.version === null ? null : `versão ${health.version}`,
        health.totalUsers === null
          ? null
          : `${String(health.totalUsers)} instâncias, ${String(health.loggedInUsers ?? 0)} conectadas`,
      ]
        .filter((part): part is string => part !== null)
        .join(' · ')
    : `Não respondeu: ${health.error ?? 'sem resposta'}`

const ServerForm = ({
  server,
  onSaved,
  onCancel,
}: {
  server: MessagingServerSummary | null
  onSaved: (health: MessagingServerHealth) => void
  onCancel: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [name, setName] = useState(server?.name ?? '')
  const [baseUrl, setBaseUrl] = useState(server?.baseUrl ?? '')
  const [adminToken, setAdminToken] = useState('')
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: async () =>
      await saveMessagingServer(server?.id ?? null, {
        name: name.trim(),
        baseUrl: baseUrl.trim(),
        adminToken: adminToken.trim() === '' ? null : adminToken.trim(),
        version: server?.version ?? 0,
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        queryKey: ['platform-messaging-servers'],
      })
      onSaved(result.health)
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar o servidor.'
      )
    },
  })

  const creating = server === null

  return (
    <Section title={creating ? 'Novo servidor' : `Editar ${server.name}`}>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          save.mutate()
        }}
      >
        <Field label="Nome" required>
          {(field) => (
            <Input
              {...field}
              value={name}
              placeholder="WuzAPI Víbora"
              onChange={(event) => {
                setName(event.target.value)
              }}
            />
          )}
        </Field>
        <Field
          label="Endereço"
          required
          hint="O endereço da API, como https://wz.exemplo.com.br/api."
        >
          {(field) => (
            <Input
              {...field}
              value={baseUrl}
              inputMode="url"
              autoComplete="off"
              onChange={(event) => {
                setBaseUrl(event.target.value)
              }}
            />
          )}
        </Field>
        <Field
          label="Token de administração"
          required={creating}
          hint={
            creating
              ? 'O WUZAPI_ADMIN_TOKEN do servidor. Fica cifrado e nunca volta à tela.'
              : 'Deixe em branco para manter o token gravado.'
          }
        >
          {(field) => (
            <Input
              {...field}
              type="password"
              autoComplete="off"
              value={adminToken}
              onChange={(event) => {
                setAdminToken(event.target.value)
              }}
            />
          )}
        </Field>
        {error === null ? null : (
          <InlineAlert tone="danger">{error}</InlineAlert>
        )}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={save.isPending}
            disabled={
              name.trim() === '' ||
              baseUrl.trim() === '' ||
              (creating && adminToken.trim().length < 8)
            }
          >
            {creating ? 'Cadastrar e testar' : 'Salvar e testar'}
          </Button>
        </div>
      </form>
    </Section>
  )
}

export const PlatformMessagingServers = ({
  onClose,
}: {
  onClose: () => void
}): ReactNode => {
  /** `null` = nenhum formulário; `'new'` = cadastrando; o servidor = editando. */
  const [editing, setEditing] = useState<MessagingServerSummary | 'new' | null>(
    null
  )
  /** O último resultado de teste por servidor — some ao fechar, de propósito. */
  const [health, setHealth] = useState<Record<string, MessagingServerHealth>>(
    {}
  )
  const [lastSaved, setLastSaved] = useState<MessagingServerHealth | null>(null)

  const servers = useQuery({
    queryKey: ['platform-messaging-servers'],
    queryFn: async ({ signal }) => await fetchMessagingServers(signal),
  })

  const test = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (serverId: string) => ({
      serverId,
      health: await testMessagingServer(serverId),
    }),
    onSuccess: (result) => {
      setHealth((previous) => ({
        ...previous,
        [result.serverId]: result.health,
      }))
    },
  })

  const rows = servers.data ?? []

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title="Servidores de WhatsApp"
        description="Os servidores WuzAPI desta instalação. As instâncias de cada um se escolhem na ficha da conta da loja."
        className="max-w-xl"
      >
        <div className="flex flex-col gap-3">
          {servers.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          ) : servers.isError ? (
            <InlineAlert tone="danger">
              Não foi possível carregar os servidores.
            </InlineAlert>
          ) : rows.length === 0 && editing === null ? (
            <p className="text-(length:--text-sm) text-(--text-muted)">
              Nenhum servidor cadastrado.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-(--border-subtle) rounded-(--radius-md) border border-(--border-default)">
              {rows.map((server) => {
                const result = health[server.id]
                return (
                  <li key={server.id} className="flex flex-col gap-1 px-3 py-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="flex min-w-0 flex-col">
                        <span className="text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                          {server.name}
                        </span>
                        <span className="truncate font-(--font-mono) text-(length:--text-xs) text-(--text-muted)">
                          {server.baseUrl}
                        </span>
                      </span>
                      <span className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          loading={
                            test.isPending && test.variables === server.id
                          }
                          onClick={() => {
                            test.mutate(server.id)
                          }}
                        >
                          Testar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setLastSaved(null)
                            setEditing(server)
                          }}
                        >
                          Editar
                        </Button>
                      </span>
                    </div>
                    {result === undefined ? null : (
                      <p
                        role="status"
                        className={
                          result.reachable
                            ? 'text-(length:--text-xs) text-(--success)'
                            : 'text-(length:--text-xs) text-(--danger)'
                        }
                      >
                        {describeHealth(result)}
                      </p>
                    )}
                  </li>
                )
              })}
            </ul>
          )}

          {editing === null ? (
            <Button
              variant="secondary"
              className="self-start"
              onClick={() => {
                setLastSaved(null)
                setEditing('new')
              }}
            >
              Cadastrar servidor
            </Button>
          ) : (
            <ServerForm
              key={editing === 'new' ? 'new' : editing.id}
              server={editing === 'new' ? null : editing}
              onCancel={() => {
                setEditing(null)
              }}
              onSaved={(result) => {
                setLastSaved(result)
                setEditing(null)
              }}
            />
          )}

          {lastSaved === null ? null : (
            <InlineAlert tone={lastSaved.reachable ? 'success' : 'warning'}>
              Salvo. {describeHealth(lastSaved)}
            </InlineAlert>
          )}
          {test.isError ? (
            <InlineAlert tone="danger">
              {test.error instanceof ApiError
                ? test.error.message
                : 'Não foi possível testar o servidor.'}
            </InlineAlert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
